<template>
  <!-- My BF Fulfilment from 2026 on (F1/F1b, F2, F3, F4): giving-api's own fulfilment
       for the BF pledge and the sow, from the ring down to Dreams & Prayers. The page
       around it keeps the header, the year dropdown and the links. -->
  <div>
    <div
      v-if="fulfilment"
      class="flex flex-col gap-[20px] md:gap-[24px] lg:gap-[28px] mt-[20px] md:mt-[24px] lg:mt-[28px]"
    >
      <div v-if="view.ring" class="flex flex-col items-center gap-[6px]">
        <!-- F2 (beside a sow) draws the amount larger than F1 and F4 do. F3, a sow
             alone, draws its own green ring with leaves once something is sown. -->
        <FulfilmentRing
          :total="view.ring.total"
          :fulfilled="view.ring.fulfilled"
          :label="'Pledge\namount fulfilled'"
          :large="view.ring.sown !== null"
          :sow="Boolean(view.ring.sow)"
          data-test="bf-fulfilment-ring"
        />
        <!-- F2: what went to the sow, once anything has. -->
        <span
          v-if="view.ring.sown > 0"
          class="-mt-[4px] px-[12px] py-[4px] rounded-full bg-orange-100 text-orange-900 text-[0.8125rem] leading-[1.125rem] font-bold"
          data-test="bf-fulfilment-ring-sown"
          >+{{ formatAmount(view.ring.sown) }} sown</span
        >
      </div>
      <!-- A couple pledge names the partner on the pledge's card, not the sow's. GET
           /bf/fulfilment carries no couple, so it comes from the pledge itself; on a
           spouse's verified pledge that names the spouse who submitted it. -->
      <FulfilmentCard
        v-for="card in view.cards"
        :key="card.key"
        :card="card"
        :couple="card.key === 'sow' ? null : couple"
        @info="infoOpen = $event"
      />
      <GiveNowButton :fund="giveNowFund" compact class="mb-[4px]" />
    </div>
    <div
      v-else-if="loadFailed"
      class="flex flex-col gap-[16px] mt-[24px]"
      data-test="bf-fulfilment-error"
    >
      <p class="text-[1rem] leading-[1.5rem] text-grey-800">
        We couldn't load your Building Fund fulfilment.
      </p>
      <BottomButton text="Try again" class="w-full" data-test="bf-fulfilment-retry" @click="load" />
    </div>
    <Loading v-else data-test="bf-fulfilment-loading" />

    <!-- F2's ⓘ explainers. A phone cannot hover, so each row's tooltip opens a sheet:
         "Amount fulfilled" has the tooltip's line alone; "Amount sown" adds the
         canvas's sticky beside the frames. -->
    <PledgeInfoSheet
      v-if="hasInfo"
      :modelValue="infoOpen === 'fulfilled'"
      test-id="bf-fulfilled-sheet"
      @update:modelValue="closeInfo"
    >
      <DsBody class="text-text-body-md text-campaign-ink">{{ accountedFor }}</DsBody>
    </PledgeInfoSheet>
    <PledgeInfoSheet
      v-if="hasInfo"
      :modelValue="infoOpen === 'sown'"
      title="What is amount sown"
      test-id="bf-sow-sheet"
      @update:modelValue="closeInfo"
    >
      <DsBody class="text-text-body-md text-campaign-ink">
        When you fulfil for your pledge to sow, it is counted towards your 5-month pledge commitment
        first.
      </DsBody>
      <DsBody class="text-text-body-md text-campaign-ink">
        What is above and beyond your 5-month pledge amount will eventually be reflected here as
        your amount fulfilled for your pledge to sow.
      </DsBody>
      <DsBody class="text-text-body-md text-campaign-ink">{{ accountedFor }}</DsBody>
    </PledgeInfoSheet>

    <!-- My Dreams & Prayers, full width between Give now and the links. It is kept
         on this device for the campaign year only (bf-dreams-prayers.js). -->
    <div
      v-if="fulfilment && year === dreamsYear"
      class="hogc-theme-campaign -mx-[16px] md:-mx-[24px] lg:-mx-[28px] mt-[24px] mb-[24px]"
    >
      <!-- Edit is brown here, as the F frames draw it; navy on the pledge pages. -->
      <DreamsPrayers button-class="[--hogc-campaign-primary:theme(colors.brown.800)]" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Body as DsBody } from '@team-hogc/hogc-one-design-system';
import BottomButton from '@/components/common/BottomButton.vue';
import Loading from '@/components/common/LoadingState.vue';
import GiveNowButton from '@/components/MyGiving/Common/GiveNowButton.vue';
import DreamsPrayers from '@/components/MyGiving/BuildingFundPledge/DreamsPrayers.vue';
import PledgeInfoSheet from '@/components/MyGiving/BuildingFundPledge/PledgeInfoSheet.vue';
import { BF_GIVE_NOW_FUND, BF_PLEDGE_YEAR } from '@/constants/MyGiving/building-fund-pledge';
import { fulfilmentCards } from '@/utils/bf-fulfilment';
import { formatPledgeAmount, pledgeSummary } from '@/utils/building-fund-pledge';
import FulfilmentCard from './FulfilmentCard.vue';
import FulfilmentRing from './FulfilmentRing.vue';

export default {
  name: 'BuildingFundFulfilment',

  components: {
    BottomButton,
    DreamsPrayers,
    DsBody,
    FulfilmentCard,
    FulfilmentRing,
    GiveNowButton,
    Loading,
    PledgeInfoSheet,
  },

  props: {
    year: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      loadFailed: false,
      // The explainer an ⓘ opened ('fulfilled' or 'sown'), or null.
      infoOpen: null,
      // The F2 tooltip's line, in both explainers.
      accountedFor: "Amount will be reflected in a few days' time after it has been accounted for.",
      giveNowFund: BF_GIVE_NOW_FUND,
      dreamsYear: BF_PLEDGE_YEAR,
    };
  },

  computed: {
    ...mapState('buildingFundPledge', ['fulfilment', 'pledge']),

    view() {
      return fulfilmentCards(this.fulfilment);
    },

    // The store's pledge is the campaign year's, so only that year shows it.
    couple() {
      return this.year === BF_PLEDGE_YEAR && this.pledge ? pledgeSummary(this.pledge).couple : null;
    },

    hasInfo() {
      return this.view.cards.some(card => card.sections.some(section => section.info));
    },
  },

  methods: {
    formatAmount: formatPledgeAmount,

    closeInfo(open) {
      if (!open) this.infoOpen = null;
    },

    // The page calls this on every entry (Ionic keeps it alive). `undefined` means
    // the call failed and the store has toasted: offer a retry.
    // The pledge is fetched alongside, quietly, for the couple line alone.
    async load() {
      this.loadFailed = false;
      const [fulfilment] = await Promise.all([
        this.$store.dispatch('buildingFundPledge/fetchFulfilment', this.year),
        this.year === BF_PLEDGE_YEAR &&
          this.$store.dispatch('buildingFundPledge/fetchPledge', { year: this.year, quiet: true }),
      ]);
      this.loadFailed = fulfilment === undefined;
    },
  },
};
</script>
